import { useLatest } from '@solinkcorp/solink-components';
import PageLoadingSpinner from 'core/components/loadingIndicator/PageLoadingSpinner';
import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { logout } from '../auth0';
import { LOGOUT_REASON } from '../authConstants';
import { useCoreTranslations } from '../translations';
import { type LogoutHandler, type UserSession } from '../types';

const MESSAGE_DELAY = 3000;

export interface LogoutProps {
  userSession: UserSession | undefined;
  logoutHandlers: { current: LogoutHandler }[];
}

const Logout: React.FC<LogoutProps> = ({ userSession, logoutHandlers }) => {
  const [searchParams] = useSearchParams();
  const i18n = useCoreTranslations();
  const latestReason = useLatest(searchParams.get('reason') || undefined);
  const latestRedirect = useLatest(searchParams.get('redirectTo') || undefined);
  const latestSession = useLatest(userSession);

  const reasonMessage = useMemo(() => {
    if (latestReason.current === 'WRONG_CUSTOMER') {
      return i18n.wrongCustomer;
    }

    return undefined;
  }, [latestReason, i18n]);

  useEffect(() => {
    const reasonKey = (latestReason.current as keyof typeof LOGOUT_REASON) || 'USER_INITIATED';
    const reason = LOGOUT_REASON[reasonKey] || LOGOUT_REASON.USER_INITIATED;

    // If we're showing a message, then set a short delay so the user understands why we're logging them out.
    setTimeout(
      () => {
        logout(latestSession.current, logoutHandlers, reason, latestRedirect.current);
      },
      reasonMessage ? MESSAGE_DELAY : 0,
    );
  }, [latestReason, latestRedirect, latestSession, logoutHandlers, reasonMessage]);

  const translations = useCoreTranslations();

  // Remove the chosen developer environment from local storage
  // This is to prevent the user from being able to switch back to the default environment
  // in the event that the user doesn't have 'developer' access in the current environment
  localStorage.removeItem('PERSISTENT_env');

  return <PageLoadingSpinner message={`${reasonMessage || translations.loggingOut}...`} />;
};

export default Logout;
